import * as React from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Collapse } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsPausedOutlinedIcon from '@mui/icons-material/NotificationsPausedOutlined';
import PeopleIcon from '@mui/icons-material/People';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import KeyIcon from '@mui/icons-material/Key';
import { useAuth } from '../../context/AuthContext';
import { Fragment, ReactElement, useState } from 'react';
import useCanAccess from '../../hooks/useCanAccess';

interface MenuItem {
    id: string;
    text: string;
    icon: ReactElement;
    path: string;
    subItems?: MenuItem[];
}

interface MenuContentProps {
    onNavigate?: () => void;
}

export default function MenuContent({ onNavigate }: MenuContentProps) {
    const location = useLocation();
    const { t } = useTranslation("common");
    const { userInfo } = useAuth();
    const { canAccessPage } = useCanAccess();

    const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});

    const handleExpandClick = (itemId: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const mainListItems: MenuItem[] = [
        { id: 'search', text: t("navbar.search"), icon: <SearchIcon fontSize="small" />, path: '/search' },
    ];

    if (canAccessPage('DASHBOARD')) {
        mainListItems.unshift({ id: 'dashboard', text: t("navbar.dashboard"), icon: <ShowChartIcon fontSize="small" />, path: '/' });
    }

    if (userInfo?.canAccessReports && canAccessPage('REPORTS')) {
        mainListItems.push({ id: 'reports', text: t("navbar.reports"), icon: <FormatListBulletedIcon fontSize="small" />, path: '/reports' });
    }

    if (userInfo?.canAccessAdministration && canAccessPage('ADMINISTRATION')) {
        const subItems = [];

        if (canAccessPage('LOCATION_ADMINISTRATION')) {
            subItems.push({ id: 'location-administration', text: t("navbar.locationAdministration"), icon: <PeopleIcon fontSize="small" />, path: '/administration/location-administration' });
        }

        if (canAccessPage('CLIENT_ALERTS')) {
            subItems.push({ id: 'alerts', text: t("navbar.clientAlerts"), icon: <NotificationsActiveIcon fontSize="small" />, path: '/administration/client-alerts' });
        }

        if (canAccessPage('IRRELEVANT_ENTRIES')) {
            subItems.push({ id: 'irrelevant-entries', text: t("navbar.irrelevantEntries"), icon: <NotificationsPausedOutlinedIcon fontSize="small" />, path: '/administration/irrelevant-entries' });
        }

        mainListItems.push({
            id: 'administration',
            text: t("navbar.administration"),
            icon: <KeyIcon fontSize="small" />,
            path: '/administration',
            subItems,
        });
    }

    const isPathActive = (path: string, subItems?: MenuItem[]) => {
        if (location.pathname === path) return true;
        if (subItems) {
            return subItems.some(subItem => location.pathname === subItem.path);
        }
        return false;
    };

    return (
        <Stack sx={{ flexGrow: 1, p: 0, justifyContent: 'space-between' }}>
            <List dense>
                {mainListItems.map((item) => (
                    <Fragment key={item.id}>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            {item.subItems ? (
                                <>
                                    <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <ListItemButton
                                            selected={isPathActive(item.path, item.subItems)}
                                            onClick={() => onNavigate?.()}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    marginRight: 2,
                                                }}
                                            >
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={item.text} slotProps={{ primary: { variant: 'body2', noWrap: true } }} />
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    flexShrink: 0,
                                                    justifyContent: 'flex-end',
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleExpandClick(item.id);
                                                }}
                                            >
                                                {expandedItems[item.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </Link>
                                    <Collapse in={expandedItems[item.id]} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {item.subItems.map((subItem) => (
                                                <ListItem key={subItem.id} disablePadding sx={{ display: 'block' }}>
                                                    <Link to={subItem.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                                        <ListItemButton
                                                            sx={{ pl: 4 }}
                                                            selected={location.pathname === subItem.path}
                                                            onClick={() => onNavigate?.()}
                                                        >
                                                            <ListItemIcon
                                                                sx={{
                                                                    minWidth: 0,
                                                                    marginRight: 2,
                                                                }}
                                                            >
                                                                {subItem.icon}
                                                            </ListItemIcon>
                                                            <ListItemText primary={subItem.text} slotProps={{ primary: { variant: 'body2' } }} />
                                                        </ListItemButton>
                                                    </Link>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Collapse>
                                </>
                            ) : (
                                <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                    <ListItemButton
                                        selected={location.pathname === item.path}
                                        onClick={() => onNavigate?.()}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                marginRight: 2,
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText primary={item.text} slotProps={{ primary: { variant: 'body2' } }} />
                                    </ListItemButton>
                                </Link>
                            )}
                        </ListItem>
                    </Fragment>
                ))}
            </List>
        </Stack>
    );
};
